import type { StoryObj } from '@storybook/html'
import type { CobaltIconButton as CobaltIconButtonProps } from './cobalt-icon-button'
import { ICONS } from '@pollux-docaposte/icon-library'

const iconsAvailable = Object.keys(ICONS)

export default {
  title: 'Components/Action/cobalt-icon-button',
  argTypes: {
    label: {
      type: { name: 'string', required: true },
      description: 'This is the text which describe the button. It is **required** for accessibility concerns. It will appear in a tooltip.'
    },
    type: {
      control: 'select',
      options: ['button', 'reset', 'submit'],
      description: "A string indicating the behavior of the button. It relies on `HTMLButtonElement['type']`",
      table: {
        defaultValue: { summary: 'button' }
      }
    },
    variant: {
      control: 'select',
      options: ['primary', 'secondary', 'tertiary', 'quaternary'],
      description: 'A string indicating the design variation of the button based on the level of importance.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'primary' }
      }
    },
    disabled: {
      control: 'boolean',
      options: [true, false],
      description: 'A boolean indicating the disable state of the button. The `aria-disabled` attribute relies on this property.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    size: {
      control: 'select',
      options: ['small', 'medium', 'large'],
      description: 'A string indicating the size variation of the button.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'medium' }
      }
    },
    iconName: {
      type: { name: 'string' },
      options: iconsAvailable,
      control: { type: 'select' },
      description:
        'Accept all icon names from `@pollux-docaposte/icon-library`. Check icons available in the control column.\nThe icon render only if `iconName` and `iconPosition` are defined.',
      table: {
        type: { summary: `${JSON.stringify(iconsAvailable).substring(0, 100)}...` }
      }
    },
    tooltipDirection: {
      control: 'select',
      options: ['top', 'right', 'bottom', 'left'],
      description: 'Tooltip alignment prop to where the tooltip will appear.',
      type: { name: 'string' },
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'top' }
      }
    },
    cobaltIconButtonClick: {
      description: 'Custom event emitted when icon button is clicked',
      table: {
        type: {
          summary: 'Integration',
          detail:
            'React: onCobaltCheckboxChange={handleClick}\nVue:@cobaltIconButtonClick="handleClick"\nAngular:(cobaltIconButtonClick)="handleClick()"\nVanillaJs:.addEventListener("cobaltIconButtonClick", (event) => {//handle click});'
        }
      },

      action: 'cobaltIconButtonClick'
    }
  }
}

const Template = (args: CobaltIconButtonProps) => {
  return `
  <div style="display: flex; align-items: center; justify-content: center; padding: 5rem;">
    <cobalt-icon-button type="${args.type}" label="${args.label}" variant="${args.variant}" disabled="${args.disabled}" size="${args.size}" icon-name="${args.iconName}" tooltip-direction="${args.tooltipDirection}"></cobalt-icon-button>
  </div>`
}

export const Default: StoryObj = Template.bind({})

Default.args = {
  label: 'Label',
  type: 'button',
  variant: 'primary',
  disabled: false,
  size: 'medium',
  iconName: 'star-solid',
  tooltipDirection: 'top'
}

const TemplateClick = (args: CobaltIconButtonProps) => {
  return `
<script>
  document.querySelector('#action-button').addEventListener('cobaltIconButtonClick', (e) => {
    alert('Custom click event fired!', e)
  })
</script>
<cobalt-icon-button id="action-button" label="${args.label}" icon-name="${args.iconName}" tooltip-direction="${args.tooltipDirection}"></cobalt-icon-button>`
}

export const OnClickEvent: StoryObj = TemplateClick.bind({})

OnClickEvent.args = {
  label: 'Label',
  iconName: 'star-solid',
  tooltipDirection: 'top'
}
